import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './LiveChat.css';

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Get user info from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setUserInfo({
        id: user.id,
        name: user.name || 'User',
        email: user.email || '',
        phone: user.phone || ''
      });
    } else {
      // Guest user
      setUserInfo({
        id: null,
        name: 'Guest',
        email: '',
        phone: ''
      });
    }
  }, []);

  // Create or get chat session
  useEffect(() => {
    if (isOpen && userInfo) {
      createOrGetSession();
    }
  }, [isOpen, userInfo]);

  // Subscribe to new messages with real-time updates
  useEffect(() => {
    if (!sessionId) return;

    console.log('Setting up real-time subscription for session:', sessionId);

    const channel = supabase
      .channel(`chat:${sessionId}`, {
        config: {
          broadcast: { self: true }
        }
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          console.log('New message received via real-time:', payload.new);
          // Only add if message doesn't already exist (avoid duplicates)
          setMessages((prev) => {
            const exists = prev.some(msg => msg.id === payload.new.id || (msg.id && msg.id.toString() === payload.new.id.toString()));
            if (exists) {
              console.log('Message already exists, skipping');
              return prev;
            }
            console.log('Adding new message to chat');
            const newMessages = [...prev, payload.new];
            // Scroll to bottom after state update
            setTimeout(() => {
              scrollToBottom();
            }, 50);
            return newMessages;
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          console.log('Message updated via real-time:', payload.new);
          setMessages((prev) =>
            prev.map(msg => msg.id === payload.new.id ? payload.new : msg)
          );
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to chat messages');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to chat messages');
        } else if (status === 'TIMED_OUT') {
          console.warn('⚠️ Subscription timed out, retrying...');
        } else if (status === 'CLOSED') {
          console.warn('⚠️ Subscription closed');
        }
      });

    // Fallback: Poll for new messages every 2 seconds if real-time fails
    const pollInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });

        if (!error && data) {
          setMessages((prev) => {
            // Only update if we have new messages
            if (data.length > prev.length) {
              // Scroll to bottom when new messages are found
              setTimeout(() => {
                scrollToBottom();
              }, 100);
              return data;
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Error polling messages:', err);
      }
    }, 2000);

    return () => {
      console.log('Cleaning up subscription for session:', sessionId);
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  // Load messages when session is ready
  useEffect(() => {
    if (sessionId) {
      loadMessages();
    }
  }, [sessionId]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const createOrGetSession = async () => {
    try {
      setIsLoading(true);

      // Check for existing active session
      let query = supabase
        .from('chat_sessions')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (userInfo.id) {
        query = query.eq('user_id', userInfo.id);
      } else {
        // For guest users, check by email or create new
        if (userInfo.email) {
          query = query.eq('user_email', userInfo.email);
        } else {
          query = query.is('user_id', null).is('user_email', null);
        }
      }

      const { data: existingSessions, error: sessionError } = await query;

      if (existingSessions && existingSessions.length > 0) {
        // Use existing session
        setSessionId(existingSessions[0].id);
        setIsLoading(false);
        return;
      }

      // Create new session
      const { data: newSession, error: createError } = await supabase
        .from('chat_sessions')
        .insert([
          {
            user_id: userInfo.id || null,
            user_name: userInfo.name,
            user_email: userInfo.email || null,
            user_phone: userInfo.phone || null,
            status: 'active'
          }
        ])
        .select()
        .single();

      if (createError) {
        console.error('Error creating session:', createError);
        throw createError;
      }
      
      if (newSession) {
        setSessionId(newSession.id);
        console.log('Chat session created:', newSession.id);
      }
    } catch (error) {
      console.error('Error creating/getting session:', error);
      alert('Failed to start chat session. Please refresh the page and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      // Scroll to bottom after messages are loaded
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !sessionId || !userInfo) return;

    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately

    // Optimistic update - add message to UI immediately
    const tempMessage = {
      id: `temp-${Date.now()}`,
      session_id: sessionId,
      message: messageText,
      sender_type: 'user',
      sender_id: userInfo.id,
      sender_name: userInfo.name,
      is_read: false,
      created_at: new Date().toISOString()
    };
    setMessages((prev) => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert([
          {
            session_id: sessionId,
            message: messageText,
            sender_type: 'user',
            sender_id: userInfo.id,
            sender_name: userInfo.name,
            is_read: false
          }
        ])
        .select()
        .single();

      if (error) throw error;

      // Replace temp message with real message from database
      setMessages((prev) => 
        prev.map(msg => 
          msg.id === tempMessage.id ? data : msg
        )
      );
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove temp message on error
      setMessages((prev) => prev.filter(msg => msg.id !== tempMessage.id));
      setNewMessage(messageText); // Restore message text
      alert('Failed to send message. Please try again.');
    }
  };

  const scrollToBottom = () => {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
      // Also try scrolling the messages container directly
      if (chatContainerRef.current) {
        const messagesContainer = chatContainerRef.current.querySelector('.live-chat-messages');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }
    }, 100);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className={`live-chat-button ${isOpen ? 'live-chat-button-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open live chat"
      >
        {isOpen ? (
          <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="live-chat-window" ref={chatContainerRef}>
          <div className="live-chat-header">
            <div className="live-chat-header-content">
              <div className="live-chat-status">
                <span className="live-chat-status-dot"></span>
                <span>Live Chat Support</span>
              </div>
            </div>
            <button
              className="live-chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="live-chat-messages">
            {isLoading && !sessionId ? (
              <div className="live-chat-loading">Connecting...</div>
            ) : messages.length === 0 ? (
              <div className="live-chat-empty">
                <p>Start a conversation with our support team!</p>
                <p className="live-chat-empty-subtitle">We're here to help you with any questions.</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`live-chat-message ${
                    message.sender_type === 'user' ? 'live-chat-message-user' : 'live-chat-message-admin'
                  }`}
                >
                  <div className="live-chat-message-content">
                    <div className="live-chat-message-text">{message.message}</div>
                    <div className="live-chat-message-time">{formatTime(message.created_at)}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="live-chat-input-form" onSubmit={sendMessage}>
            <input
              type="text"
              className="live-chat-input"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={!sessionId || isLoading}
            />
            <button
              type="submit"
              className="live-chat-send-button"
              disabled={!newMessage.trim() || !sessionId || isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default LiveChat;
